import BiotechIcon from "@mui/icons-material/Biotech";
import EventNoteIcon from "@mui/icons-material/EventNote";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import MedicationIcon from "@mui/icons-material/Medication";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PetsIcon from "@mui/icons-material/Pets";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RoomPreferencesIcon from "@mui/icons-material/RoomPreferences";
import { ReactComponent as AppointmentsIcon } from "../../assets/images/svg/calendar.svg";
import { ReactComponent as HeartIcon } from "../../assets/images/svg/heart.svg";
// import { ReactComponent as HomeIcon } from "../../assets/images/svg/home.svg";
import { ReactComponent as InventoryIcon } from "../../assets/images/svg/inventory-management.svg";
import { ReactComponent as PaymentIcon } from "../../assets/images/svg/invoice.svg";
import { ReactComponent as PetIcon } from "../../assets/images/svg/pet.svg";
import { ReactComponent as VetIcon } from "../../assets/images/svg/stethescope.svg";
import { readMandateSession } from "../../util/mandateSessionStorage";
import { pickPrimaryRole } from "../../util/rolePriority";

/** Roles that always see the full clinic menu (not filtered by module permissions). */
const UNFILTERED_ROLES = new Set([
  "ROLE_ADMIN",
  "ROLE_CLINIC",
  "ROLE_CLINICSUPERADMIN",
]);

/** Base clinic tabs; `moduleKey` maps to role-module-permissions.modules. */
const navClinicConfig = [
  // {
  //   title: "Dashboard",
  //   path: "/dashboard",
  //   icon: <HomeIcon />,
  //   moduleKey: "dashboard",
  // },
  {
    title: "Dashboard",
    path: "/dashboard",
    icon: <AppointmentsIcon />,
    moduleKey: "dashboard",
  },
  { title: "Vets", path: "/vets", icon: <VetIcon />, moduleKey: "vets" },
  { title: "Pets", path: "/pets", icon: <PetIcon />, moduleKey: "pets" },
  // {
  //   title: "Appointments",
  //   path: "/appointments",
  //   icon: <AppointmentsIcon />,
  //   moduleKey: "dashboard",
  // },
  {
    title: "Billing",
    path: "/payments",
    icon: <PaymentIcon />,
    moduleKey: "billing",
  },
  {
    title: "Inventory",
    path: "/inventory",
    icon: <InventoryIcon />,
    moduleKey: "inventory",
  },
  {
    title: "Diagnostics",
    path: "/labdashboard",
    icon: <BiotechIcon />,
    moduleKey: "lab",
  },
  {
    title: "Pharmacy",
    path: "/phramacydashboard",
    icon: <MedicationIcon />,
    moduleKey: "pharmacy",
  },
  { title: "Plan", path: "/plan", icon: <HeartIcon />, moduleKey: "plan" },
  {
    title: "Reports",
    path: "/reports",
    icon: <PaymentIcon />,
    moduleKey: "reports",
  },
  {
    title: "Knowledge Base",
    path: "/knowledge-base",
    icon: <MenuBookIcon />,
    moduleKey: null,
  },
];

const navAdminConfig = [
  { title: "clinics", path: "/home", icon: <LocalHospitalIcon /> },
  { title: "pets", path: "/pet", icon: <PetsIcon /> },
  { title: "payments", path: "/payment", icon: <ReceiptLongIcon /> },
];

const navManagementConfig = [
  { title: "dashboard", path: "/home", icon: <HomeOutlinedIcon /> },
  { title: "vets", path: "/vets", icon: <LocalHospitalIcon /> },
  { title: "pets", path: "/pets", icon: <PetsIcon /> },
  { title: "appointments", path: "/appointments", icon: <EventNoteIcon /> },
  { title: "payments", path: "/payments", icon: <ReceiptLongIcon /> },
  {
    title: "pharma delivery",
    path: "/pharma-delivery",
    icon: <LocalShippingIcon />,
  },
  { title: "settings", path: "/settings", icon: <RoomPreferencesIcon /> },
  { title: "inventory", path: "/inventory", icon: <MedicalServicesIcon /> },
];

const navVeterinaryConfig = [
  { title: "dashboard", path: "/dashboard", icon: <HomeOutlinedIcon /> },
];

/** In-memory cache so brief empty session reads don't flash-hide tabs. */
let lastRolePermissions = [];
let lastClinicRoleName = null;
const lastModulesByRole = new Map();

const getRolePermissionsList = () => {
  const list = readMandateSession()?.roleModulePermissions?.rolePermissions;
  const fresh = Array.isArray(list) ? list : [];
  if (fresh.length > 0) {
    lastRolePermissions = fresh;
    return fresh;
  }
  return lastRolePermissions;
};

const getClinicPermissionRoleName = () => {
  const session = readMandateSession();
  if (
    session?.currentUserClinicRole &&
    typeof session.currentUserClinicRole === "string"
  ) {
    lastClinicRoleName = session.currentUserClinicRole;
    return session.currentUserClinicRole;
  }

  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    for (const field of ["role", "roleName", "clinicRole"]) {
      const value = user?.[field];
      if (value && typeof value === "string" && !value.startsWith("ROLE_")) {
        lastClinicRoleName = value;
        return value;
      }
    }

    const userId = user?.doctorId ?? user?.id ?? localStorage.getItem("userId");
    const vets = Array.isArray(session?.vets) ? session.vets : [];
    if (userId && vets.length > 0) {
      const vet = vets.find((v) =>
        [v?.doctorId, v?.id, v?.userId].some(
          (id) => id != null && String(id) === String(userId),
        ),
      );
      const name = vet?.role ?? vet?.roleName ?? null;
      if (name) {
        lastClinicRoleName = name;
        return name;
      }
    }
  } catch {
    // ignore
  }

  return lastClinicRoleName;
};

/**
 * Resolve modules for a ROLE_* authority.
 * Prefer matching by role code; use clinic role name only to disambiguate
 * duplicates (e.g. Veterinarian vs Paravet/Nurse both ROLE_DOCTOR).
 */
const getModulesForUserRole = (userRole) => {
  const permissions = getRolePermissionsList();
  if (!permissions.length) return null;

  const matches = permissions.filter((item) => item?.role === userRole);
  const permissionName = getClinicPermissionRoleName();

  if (matches.length === 1 && matches[0]?.modules) {
    lastModulesByRole.set(userRole, matches[0].modules);
    return matches[0].modules;
  }

  if (matches.length > 1) {
    if (permissionName) {
      const byName = matches.find((item) => item?.name === permissionName);
      if (byName?.modules) {
        lastModulesByRole.set(userRole, byName.modules);
        return byName.modules;
      }
    }
    // Prefer Veterinarian when name is unknown (common ROLE_DOCTOR case).
    const preferred =
      matches.find((item) => item?.name === "Veterinarian") || matches[0];
    if (preferred?.modules) {
      lastModulesByRole.set(userRole, preferred.modules);
      return preferred.modules;
    }
  }

  // Fallback when permissions list has no entry for this ROLE_* code.
  if (permissionName) {
    const byName = permissions.find((item) => item?.name === permissionName);
    if (byName?.modules) {
      lastModulesByRole.set(userRole, byName.modules);
      return byName.modules;
    }
  }

  return lastModulesByRole.get(userRole) ?? null;
};

const isNavItemAllowed = (nav, userRole) => {
  if (UNFILTERED_ROLES.has(userRole)) return true;
  if (!nav?.moduleKey) return true;

  const modules = getModulesForUserRole(userRole);
  // No permission data yet → keep tabs (avoid empty flash before mandate loads).
  if (!modules) return true;

  const access = modules[nav.moduleKey];
  if (access == null || access === "") return true;
  return access !== "none";
};

const filterNavByPermissions = (items, userRole) =>
  (items || []).filter((nav) => isNavItemAllowed(nav, userRole));

const buildClinicNavForRole = (userRole) =>
  filterNavByPermissions(navClinicConfig, userRole);

const navVetConfig = buildClinicNavForRole("ROLE_DOCTOR");
const navBillingConfig = buildClinicNavForRole("ROLE_BILLING");
const navFrontDeskConfig = buildClinicNavForRole("ROLE_RECEPTION");
const navInventoryConfig = buildClinicNavForRole("ROLE_INVENTORY");
const navPharmacyConfig = buildClinicNavForRole("ROLE_PHARMACY");
const navGroomerConfig = buildClinicNavForRole("ROLE_GROOMER");
const navLabConfig = buildClinicNavForRole("ROLE_RADIOLOGY");

const getNavConfigForRole = (userRole) => {
  if (userRole === "ROLE_ADMIN") return navAdminConfig;
  if (userRole === "ROLE_CLINIC" || userRole === "ROLE_CLINICSUPERADMIN") {
    return navClinicConfig;
  }
  if (userRole === "ROLE_CLINICADMIN") {
    return buildClinicNavForRole("ROLE_CLINICADMIN");
  }
  if (userRole === "ROLE_INVENTORY") return buildClinicNavForRole(userRole);
  if (userRole === "ROLE_BILLING") return buildClinicNavForRole(userRole);
  if (userRole === "ROLE_RECEPTION") return buildClinicNavForRole(userRole);
  if (userRole === "ROLE_GROOMER") return buildClinicNavForRole(userRole);
  if (userRole === "ROLE_RADIOLOGY") return buildClinicNavForRole(userRole);
  if (userRole === "ROLE_PHARMACY") return buildClinicNavForRole(userRole);
  if (userRole === "ROLE_DOCTOR") return buildClinicNavForRole(userRole);
  return buildClinicNavForRole("ROLE_DOCTOR");
};

const getNavConfigForRoles = (roles = []) => {
  const primaryRole =
    pickPrimaryRole(roles) || localStorage.getItem("role") || "ROLE_DOCTOR";
  return getNavConfigForRole(primaryRole);
};

const navPathsEqual = (prev = [], next = []) =>
  prev.length === next.length &&
  prev.every((item, index) => item?.path === next[index]?.path);

export {
  getNavConfigForRole,
  getNavConfigForRoles,
  navAdminConfig,
  navBillingConfig,
  navClinicConfig,
  navFrontDeskConfig,
  navGroomerConfig,
  navInventoryConfig,
  navLabConfig,
  navManagementConfig,
  navPathsEqual,
  navPharmacyConfig,
  navVetConfig,
  navVeterinaryConfig,
};
